import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom';
import SkySparkGrid from '../../components/api/SkySparkGrid';
import { Box, Grid, Paper, Typography, LinearProgress, TextField, Button, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import InfLoader from '../../components/infLoad/index.jsx';
import DataUnavailable from '../../components/data/DataUnavailable.jsx';
import historianGroen from '../../img/historian-k-groen.svg';

const prettify = (str) => str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const LiveTable = () => {
    const { siteId } = useParams();
    const { tablePageRef } = useParams();
    const { tableRefreshTime } = useParams();
    const [tablesLoading, setTablesLoading] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [fetchingData, setfetchingData] = useState(false);
    // Add search state for each table (by index)
    const [searchTexts, setSearchTexts] = useState({});

    // Fetch table data when the component mounts
    useEffect(() => {
        setTableData([]);
        const fetchTableData = async () => {
            setfetchingData(true);
            await SkySparkGrid(`inf_api_query(@${siteId}, "infGijsReadAllTables(@${tablePageRef})")`)
                .then((response) => {
                    const rows = response.data.rows;
                    if (rows.length > 0) {
                        setTableData(rows)
                        setTablesLoading(false);
                    }
                    else setTablesLoading(false);
                })
                .catch((error) => console.error("Error fetching data:", error));
            await new Promise(resolve => setTimeout(resolve, 1000));
            setfetchingData(false);
        }
        fetchTableData()
        const intervalId = setInterval(fetchTableData, tableRefreshTime * 1000);
        return () => clearInterval(intervalId);
    }, [tablePageRef, siteId, tableRefreshTime])

    // Create a global order of unique column names across all tables
    // This ensures consistent column ordering across different tables
    const globalColumnNames = useMemo(() => {
        const seen = new Set();  // Track unique column names
        const order = [];        // Store the order of columns

        // Iterate through all tables and their rows to collect unique column names
        tableData.forEach((table) => {
            table.tableRows.forEach((rec) => {
                Object.keys(rec).forEach((col) => {
                    if (!seen.has(col)) {
                        seen.add(col);
                        order.push(col);
                    }
                });
            });
        });

        // Ensure 'id' is always the first column
        const idx = order.indexOf("id");
        if (idx > 0) {
            order.splice(idx, 1);
            order.unshift("id");
        }

        return order;
    }, [tableData]);

    // Create column definitions for the DataGrid component
    // Maps each column name to a configuration object
    const columns = useMemo(
        () =>
            globalColumnNames.map((field) => ({
                field,
                headerName: prettify(field),  // Use prettified name for display
                width: field === "id" ? 230 : 150,  // Wider column for ID
            })),
        [globalColumnNames]
    );

    // Sort tableData alphabetically by table title
    const sortedTableData = useMemo(() => {
        return [...tableData].sort((a, b) => {
            const titleA = a.id.dis.toLowerCase();
            const titleB = b.id.dis.toLowerCase();
            return titleA.localeCompare(titleB);
        });
    }, [tableData]);

    return (
        <Box m="18px">
            {tableData.length === 0 && tablesLoading === true ?  (
            <Box sx={{width:'100%', height:{ xl: '700px', lg: '500px', md: '400px' }, display:'flex', alignItems:'center', justifyContent:'center'}}>
                <InfLoader/>
            </Box>
            ) : (
                tableData.length > 0 && tablesLoading === false ? (
                    // Create a responsive grid layout for tables
                    <Grid container spacing={3}>
                        {sortedTableData.map((table, ti) => {
                            // Transform table rows into a format suitable for DataGrid
                            const rows = table.tableRows.map((rec) => {
                                const flat = {};

                                // Process each column in the row
                                globalColumnNames.forEach((col) => {
                                    const cell = rec[col];
                                    // If cell is a number, round to 1 decimal
                                    if (cell && typeof cell.val === "number") {
                                        const rounded = Math.round(cell.val * 10) / 10;
                                        // Display with unit if available
                                        flat[col] = cell.unit
                                            ? `${rounded.toFixed(1)} ${cell.unit}`
                                            : rounded.toFixed(1);
                                    } else {
                                        // For references and other types, use display value or fallback
                                        flat[col] = cell?.dis ?? cell?.val ?? "-";
                                    }
                                });

                                // Set unique ID for DataGrid row
                                flat.id = rec.id.dis;
                                return flat;
                            });

                            // Filter rows based on search text for this table
                            const searchText = searchTexts[ti] || "";
                            const filteredRows = searchText
                                ? rows.filter(row =>
                                    Object.values(row).some(val =>
                                        String(val).toLowerCase().includes(searchText.toLowerCase())
                                    )
                                )
                                : rows;

                            // Per row add 70px height to table, Min height is 200px
                            let tableHeight = table['tableRows'].length * 70

                            return (
                                // Each table is rendered in a grid item
                                // xs={12} means full width on mobile, md={6} means half width on medium+ screens
                                <Grid item xs={12} key={ti}>
                                    <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                                        {/* Table title with custom styling */}
                                        <Typography
                                            variant="h5"
                                            gutterBottom
                                            sx={{
                                                color: 'black',
                                                fontWeight: 'bold',
                                                mb: 2,
                                                borderBottom: '2px solid',
                                                borderColor: 'black',
                                                pb: 1
                                            }}
                                        >
                                            {table.id.dis}
                                        </Typography>
                                        {/* Search bar and historian button */}
                                        <Box mb={2} sx={{width:'100%', display:'flex', gap: 1, alignItems: 'center'}}>
                                            <TextField
                                                label={searchText ? " " : "Zoeken"}
                                                fullWidth
                                                variant="outlined"
                                                value={searchText}
                                                onChange={e => setSearchTexts(st => ({ ...st, [ti]: e.target.value }))}
                                                InputLabelProps={{shrink: false, sx: { mt: '-10px' }}}
                                                InputProps={{ sx: { height: '30px' } }}
                                            />
                                            <Tooltip 
                                                title="Bekijk historische data van deze tabel" 
                                                arrow
                                                placement="top"
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            backgroundColor: '#f2f0f0',
                                                            color: 'black',
                                                            fontSize: '12px',
                                                            fontWeight: 'bold',
                                                            '& .MuiTooltip-arrow': {
                                                                color: '#adadad'
                                                            }
                                                        }
                                                    }
                                                }}
                                            >
                                                <Button 
                                                    sx={{
                                                        height: '30px', 
                                                        minWidth: '30px', 
                                                        p: 0,
                                                        backgroundColor: '#f5f5f5',
                                                        border: '1px solid #e0e0e0',
                                                        '&:hover': {
                                                            backgroundColor: '#e8f5e8',
                                                            border: '1px solid #4caf50'
                                                        }
                                                    }} 
                                                    onClick={async () => {
                                                        await SkySparkGrid(`inf_api_query(@${siteId}, "infGijsGetTableShellLink(@${table["id"]["val"]})")`).then((response) => {
                                                            window.open("https://infinity.skyspark.nl" + response["data"]["rows"][0]["val"]["val"])
                                                        })
                                                    }}
                                                >
                                                    <img src={historianGroen} alt="Historian" style={{ height: '20px', width: '20px', objectFit: 'contain' }} />
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                        {/* DataGrid component for displaying the table data */}
                                        <Box sx={{ height: tableHeight, minHeight:"200px" , width: "100%",  '.MuiLinearProgress-bar': {backgroundColor: '#00E886'} }}>
                                            <DataGrid
                                                columns={columns}
                                                rows={filteredRows}
                                                pageSize={5}
                                                loading={fetchingData}
                                                rowsPerPageOptions={[5]}
                                                slots={{
                                                    noRowsOverlay: () => (
                                                        <Box sx={{width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center" }}>
                                                            <Typography variant='h5' color="gray">Geen rows gevonden</Typography>
                                                        </Box>
                                                    ),
                                                    loadingOverlay: LinearProgress,
                                                }}
                                                disableSelectionOnClick
                                                sx={{
                                                    // Color styles for different values
                                                    '& .good': {
                                                        color: '#57d147',
                                                        fontWeight: 'bold',
                                                    },
                                                    '& .average': {
                                                        color: '#FF5F15',
                                                        fontWeight: 'bold',
                                                    },
                                                    '& .bad': {
                                                        color: '#cf3c3c',
                                                        fontWeight: 'bold',
                                                    },
                                                    // Make all cell values bold
                                                    '& .MuiDataGrid-cell': {
                                                        fontWeight: 'bold',
                                                    },
                                                    // Keep column headers normal weight
                                                    '& .MuiDataGrid-columnHeader': {
                                                        fontWeight: 'normal',
                                                    }
                                                }}
                                                getCellClassName={(params) => {
                                                    // Temperature color coding - comparing with setpoint
                                                    const fieldName = params.field.toLowerCase();
                                                    if (fieldName.includes('temp') && fieldName.includes('gemeten')) {
                                                        const measuredTemp = parseFloat(params.value);
                                                        // Find the corresponding setpoint temperature in the same row
                                                        const setpointField = Object.keys(params.row).find(key =>
                                                            key.toLowerCase().includes('temp') &&
                                                            key.toLowerCase().includes('setp')
                                                        );
                                                        
                                                        if (setpointField) {
                                                            let setpointTemp = parseFloat(params.row[setpointField]);
                                                            // If no setpoint default to 21°C
                                                            if(isNaN(setpointTemp)) setpointTemp = 21;
                                                            const difference = Math.abs(measuredTemp - setpointTemp);
    
                                                            if (difference <= 1) return 'good';      // Within 1°C of setpoint
                                                            if (difference <= 2) return 'average';   // Within 2°C of setpoint
                                                            return 'bad';                           // More than 2°C from setpoint
                                                        }
                                                    }
    
                                                    // CO2 color coding
                                                    if (params.field.toLowerCase().includes('co2')) {
                                                        const co2 = parseFloat(params.value);
                                                        if (co2 <= 800) return 'good';
                                                        if (co2 > 800 && co2 <= 1200) return 'average';
                                                        return 'bad';
                                                    }
    
                                                    // Humidity color coding
                                                    if (params.field.toLowerCase().includes('humidity') || params.field.toLowerCase().includes('vocht')) {
                                                        const humidity = parseFloat(params.value);
                                                        if (humidity >= 40 && humidity <= 60) return 'good';
                                                        if ((humidity >= 30 && humidity < 40) || (humidity > 60 && humidity <= 70)) return 'average';
                                                        return 'bad';
                                                    }
    
                                                    return '';
                                                }}
                                            />
                                        </Box>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                        <DataUnavailable message='Geen tabellen gevonden op pagina'/>
                    </Box>
                )
            )}
        </Box>
    );
}

export default LiveTable